import React, {useEffect, useCallback, RefObject} from 'react';
import cx from 'classnames';
import {Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {keys} from '../../../../../constants/KeyCodes';
import styles from './BooleanInput.module.scss';

type Props = {
    value: boolean;
    handleChange: (val: boolean) => void;
    inputRef: RefObject<HTMLInputElement>;
    isActive: boolean;
    isReadonly: boolean;
    searchMode: boolean;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    themeStyle?: 'light' | 'dark';
};

export const BooleanInput = ({
    value = false,
    handleChange,
    inputRef,
    isActive,
    isReadonly,
    searchMode,
    onKeyDown,
    themeStyle = 'light'
}: Props) => {
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case keys.SPACE:
                    e.preventDefault();
                    !isReadonly && handleChange(!value);
                    break;
                default:
                    break;
            }
        },
        [handleChange, isReadonly, value]
    );

    const handleClick = (e: React.MouseEvent) => {
        if (isReadonly) return;
        // Prevent event from bubbling to grid/table to avoid duplicate focus change calls
        e.stopPropagation();
        e.preventDefault();

        handleChange(!value);
    };

    useEffect(() => {
        isActive && document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, isActive]);

    return (
        <div
            className={cx(styles.inputWrapper, {
                [styles.darkTheme]: themeStyle === 'dark',
                [styles.lightTheme]: themeStyle === 'light',
                [styles.active]: isActive
            })}
            onClick={handleClick}
            tabIndex={0}
            ref={inputRef}
            onKeyDown={onKeyDown}
        >
            <Icon
                icon={value && PriorityIcon.check}
                componentClasses={cx(
                    styles.checkbox,
                    {[styles.readonly]: isReadonly && isActive},
                    {[styles.search]: searchMode && isActive}
                )}
            />
        </div>
    );
};
